"use client";

import React, { useState, useEffect } from "react";
import { Sparkles, ArrowRight, BookOpen, Layers } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
function ProgramIntroConsumer({
  lang
}: {
  lang: "en" | "vi";
}) {
  const {
    posts,
    loading,
    error,
    refetch
  } = usePosts();
  if (loading) {
    return <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center py-6">
        <div className="lg:col-span-7 space-y-4">
          <div className="h-6 w-36 rounded-md bg-muted animate-pulse" />
          <div className="h-10 w-3/4 rounded-md bg-muted animate-pulse" />
          <div className="h-20 w-full rounded-md bg-muted animate-pulse" />
          <div className="h-16 w-5/6 rounded-md bg-muted animate-pulse" />
        </div>
        <div className="lg:col-span-5">
          <div className="h-72 w-full rounded-xl bg-muted animate-pulse" />
        </div>
      </div>;
  }
  if (error) {
    return <div className="p-6 rounded-xl border border-destructive/30 bg-destructive/5 text-center">
        <p className="text-sm text-destructive mb-3">
          {lang === "en" ? "Unable to load program details." : "Không thể tải thông tin chương trình."}
        </p>
        <Button variant="outline" size="sm" onClick={() => refetch()}>
          {lang === "en" ? "Retry" : "Thử lại"}
        </Button>
      </div>;
  }

  // Find LEGO Robotics program entry (or closest match)
  const legoProgram = posts.find(p => {
    const progName = (p.customFields?.program_name || p.title || "").toLowerCase();
    return progName.includes("lego robotics") || progName.includes("lego");
  }) || posts[0];
  if (!legoProgram) {
    return <div className="p-8 text-center text-muted-foreground">
        {lang === "en" ? "No program introduction currently available." : "Hiện chưa có thông tin giới thiệu chương trình."}
      </div>;
  }
  const programName = legoProgram.customFields?.program_name || legoProgram.title;
  const tagline = legoProgram.customFields?.tagline;
  const whoItsFor = legoProgram.customFields?.who_its_for;
  const heroImageSrc = legoProgram.customFields?.hero_image;

  // Custom post type image object or fallback image
  const displayImage = heroImageSrc ? {
    src: heroImageSrc,
    srcset: "",
    sizes: ""
  } : {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/roboto_ecosystem_diagram.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/roboto_ecosystem_diagram.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/roboto_ecosystem_diagram.png-wvc-sizes"
  };
  return <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-center">
      {/* Program Details Column */}
      <div className="lg:col-span-7 flex flex-col items-start">
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-muted border border-border text-xs font-mono tracking-wider text-muted-foreground uppercase mb-4">
          <BookOpen className="size-3.5 text-secondary" />
          <span>
            {lang === "en" ? "PROGRAM INTRODUCTION" : "GIỚI THIỆU CHƯƠNG TRÌNH"}
          </span>
        </div>

        <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-foreground mb-4">
          {programName}
        </h2>

        {tagline && <p className="text-lg sm:text-xl font-medium text-primary font-sans leading-relaxed mb-4">
            {tagline}
          </p>}

        {whoItsFor && <div className="p-5 sm:p-6 rounded-xl bg-card border border-border shadow-xs mb-6 w-full">
            <span className="text-xs font-mono uppercase tracking-wider text-secondary font-bold block mb-2">
              {lang === "en" ? "TARGET AUDIENCE & FIT" : "ĐỐI TƯỢNG PHÙ HỢP"}
            </span>
            <p className="text-sm sm:text-base text-muted-foreground leading-relaxed whitespace-pre-wrap">
              {whoItsFor}
            </p>
          </div>}

        <div className="flex flex-wrap items-center gap-3 pt-2">
          {legoProgram.link && <Button asChild className="bg-primary text-primary-foreground hover:bg-primary/90">
              <Link to={legoProgram.link}>
                <span>{lang === "en" ? "Explore Program Details" : "Xem Chi Tiết Lộ Trình"}</span>
                <ArrowRight className="size-4" />
              </Link>
            </Button>}

          <Button asChild variant="outline">
            <Link to="/FreeTrialContactPage">
              <span>{lang === "en" ? "Book a Free Trial" : "Đăng Ký Học Thử"}</span>
            </Link>
          </Button>
        </div>
      </div>

      {/* Program Supporting Visual Column */}
      <div className="lg:col-span-5 w-full">
        <div className="relative rounded-2xl overflow-hidden border border-border bg-card shadow-md group">
          {/* Top spec-rail accent */}
          <div className="h-1.5 w-full bg-secondary" />

          <div className="relative overflow-hidden aspect-[4/3] bg-muted">
            <img src={displayImage.src} data-wvc-srcset={displayImage.srcset} data-wvc-sizes={displayImage.sizes} alt={typeof programName === "string" ? `${programName} - ROBOTO Academy` : "LEGO Robotics Program"} loading="lazy" className="w-full h-full object-cover object-center group-hover:scale-105 transition-transform duration-500" />
            <div className="absolute inset-0 bg-gradient-to-t from-foreground/60 via-transparent to-transparent opacity-40 pointer-events-none" />
          </div>

          <div className="p-4 sm:p-5 flex items-center justify-between border-t border-border bg-background/90">
            <div className="flex items-center gap-2">
              <span className="size-2 rounded-full bg-secondary animate-pulse" />
              <span className="text-xs font-mono font-semibold text-foreground uppercase">
                {programName}
              </span>
            </div>
            <Badge variant="secondary" className="text-xs">
              {lang === "en" ? "Hands-on Lab" : "Thực Hành Trực Tiếp"}
            </Badge>
          </div>
        </div>
      </div>
    </div>;
}
export default function ProgramEcosystem() {
  const [lang, setLang] = useState<"en" | "vi">("vi");
  useEffect(() => {
    const getStoredLang = (): "en" | "vi" => {
      try {
        const stored = localStorage.getItem("roboto_lang");
        if (stored === "en" || stored === "vi") return stored;
      } catch {
        // Fallback to default
      }
      return "vi";
    };
    setLang(getStoredLang());
    const handleLangChange = (e: Event) => {
      const customEv = e as CustomEvent;
      if (customEv.detail && (customEv.detail === "en" || customEv.detail === "vi")) {
        setLang(customEv.detail);
      } else {
        setLang(getStoredLang());
      }
    };
    window.addEventListener("roboto-lang-change", handleLangChange);
    window.addEventListener("storage", handleLangChange);
    return () => {
      window.removeEventListener("roboto-lang-change", handleLangChange);
      window.removeEventListener("storage", handleLangChange);
    };
  }, []);
  const wp_query_params = {
    post_type: "program",
    per_page: 20,
    status: ["publish"]
  };
  const wp_query = new WP_Query(wp_query_params);
  return <section id="program-introduction" data-nav="light" className="relative overflow-hidden bg-background text-foreground py-16 lg:py-24 border-b border-border">
      {/* Target anchor fallback for backwards compatibility */}
      <div id="programecosystem" aria-hidden="true" className="absolute top-0 left-0" />
      <div id="program-ecosystem" aria-hidden="true" className="absolute top-0 left-0" />

      {/* Blueprint Spec-Rail Accent Grid Layer */}
      <div className="pointer-events-none absolute inset-0 z-0 opacity-20" aria-hidden="true">
        <div className="h-full w-full" style={{
        backgroundImage: `
              linear-gradient(to right, var(--border) 1px, transparent 1px),
              linear-gradient(to bottom, var(--border) 1px, transparent 1px)
            `,
        backgroundSize: "40px 40px"
      }} />
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full max-w-7xl h-full border-x border-border/50" />
      </div>

      {/* Spec-rail Glowing Accents */}
      <div className="pointer-events-none absolute -top-24 left-1/2 -translate-x-1/2 size-96 rounded-full bg-secondary/10 blur-3xl z-0" aria-hidden="true" />
      <div className="pointer-events-none absolute -bottom-24 right-10 size-80 rounded-full bg-accent/10 blur-3xl z-0" aria-hidden="true" />

      <div className="container relative z-10 mx-auto px-4 sm:px-6 lg:px-8">
        <PostsProvider wp_query={{
        post_type: "program",
        per_page: 20,
        status: ["publish"]
      }} className="w-full">
          <ProgramIntroConsumer lang={lang} />
        </PostsProvider>
      </div>
    </section>;
}